import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { QUOTE_STATUS, fmtEur, fmtDate } from "@/lib/domain";
import { Chip } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Download, Trash2, FileText, Edit, Sparkles, Mail, Wand2, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [objects, setObjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyQuote());

  const load = async () => {
    const [q, c, o] = await Promise.all([api.get("/quotes"), api.get("/clients"), api.get("/objects")]);
    setQuotes(q.data); setClients(c.data); setObjects(o.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(emptyQuote()); setOpen(true); };
  const startEdit = (q) => { setEditing(q); setForm({ ...emptyQuote(), ...q }); setOpen(true); };

  const save = async () => {
    if (!form.client_id || !form.project_title) return toast.error("Клиент и название обязательны");
    try {
      const payload = {
        ...form,
        items: (form.items || []).map((it) => ({
          description: it.description,
          quantity: Number(it.quantity) || 0,
          unit: it.unit || "Stk",
          unit_price: Number(it.unit_price) || 0,
        })),
      };
      delete payload.id; delete payload.created_at; delete payload.quote_number;
      if (editing) {
        await api.patch(`/quotes/${editing.id}`, payload);
        toast.success("Коммерческое предложение обновлено");
      } else {
        await api.post("/quotes", payload);
        toast.success("Коммерческое предложение создано");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Ошибка: " + (e.response?.data?.detail || e.message));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Удалить КП?")) return;
    await api.delete(`/quotes/${id}`);
    load();
  };

  const downloadPdf = async (q) => {
    const token = localStorage.getItem("app_token");
    const res = await fetch(`${BACKEND_URL}/api/quotes/${q.id}/pdf`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return toast.error("Не удалось создать PDF");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Angebot_${q.quote_number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = (q) => {
    const sub = (q.items || []).reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
    const vat = q.reverse_charge_13b ? 0 : sub * 0.19;
    return { sub, vat, total: sub + vat };
  };

  return (
    <div className="space-y-6">
      <Toaster theme="dark" position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-400/80 mb-2 font-medium">Коммерческие предложения</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl">Коммерческие предложения</h1>
          <p className="text-slate-400 mt-1 text-sm">Немецкие КП с §13b UStG · экспорт в PDF</p>
        </div>
        <Button data-testid="new-quote-button" onClick={startNew} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
          <Plus size={16} /> Новое предложение
        </Button>
      </div>

      <div className="rounded-xl border border-[#212b3d] bg-[#131924] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0f1521] text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-medium">№</th>
                <th className="text-left px-4 py-3 font-medium">Проект</th>
                <th className="text-left px-4 py-3 font-medium">Клиент</th>
                <th className="text-left px-4 py-3 font-medium">Дата</th>
                <th className="text-right px-4 py-3 font-medium">Сумма (netto)</th>
                <th className="text-left px-4 py-3 font-medium">Статус</th>
                <th className="text-right px-4 py-3 font-medium">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#212b3d]">
              {quotes.length === 0 && (
                <tr><td colSpan="7" className="text-center py-10 text-slate-500"><FileText className="mx-auto mb-2 opacity-40" size={28} />КП пока нет</td></tr>
              )}
              {quotes.map((q) => {
                const c = clients.find((x) => x.id === q.client_id);
                const t = totals(q);
                return (
                  <tr key={q.id} data-testid={`quote-row-${q.id}`} className="hover:bg-[#161d2b]">
                    <td className="px-4 py-3 font-mono text-slate-300">{q.quote_number}</td>
                    <td className="px-4 py-3 font-medium">{q.project_title}
                      {q.reverse_charge_13b && <Chip className="ml-2 bg-purple-500/15 text-purple-300 border-purple-500/30">§13b</Chip>}
                    </td>
                    <td className="px-4 py-3 text-slate-300">{c?.company || c?.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{q.date}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtEur(t.sub)}</td>
                    <td className="px-4 py-3"><Chip className={QUOTE_STATUS[q.status]?.cls}>{QUOTE_STATUS[q.status]?.label}</Chip></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button data-testid={`quote-email-${q.id}`} size="icon" variant="outline"
                          onClick={async () => {
                            if (!window.confirm(`Отправить предложение ${q.quote_number} клиенту на email?`)) return;
                            try {
                              await api.post(`/quotes/${q.id}/send-to-client`);
                              toast.success("Предложение отправлено клиенту");
                              load();
                            } catch (e) { toast.error(e.response?.data?.detail || "Ошибка отправки"); }
                          }}
                          className="bg-transparent border-[#212b3d] hover:bg-blue-500/10 hover:text-blue-400 hover:border-blue-500/40 h-8 w-8"
                          title="Отправить клиенту">
                          <Mail size={14} />
                        </Button>
                        <Button data-testid={`quote-pdf-${q.id}`} size="icon" variant="outline"
                          onClick={() => downloadPdf(q)}
                          className="bg-transparent border-[#212b3d] hover:bg-amber-500/10 hover:text-amber-400 hover:border-amber-500/40 h-8 w-8">
                          <Download size={14} />
                        </Button>
                        <Button size="icon" variant="outline" onClick={() => startEdit(q)}
                          className="bg-transparent border-[#212b3d] hover:bg-[#1c2434] h-8 w-8">
                          <Edit size={14} />
                        </Button>
                        <Button size="icon" variant="outline" onClick={() => del(q.id)}
                          className="bg-transparent border-[#212b3d] hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/40 h-8 w-8">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131924] border-[#212b3d] text-slate-100 max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? `Предложение ${editing.quote_number}` : "Новое предложение"}</DialogTitle></DialogHeader>
          <QuoteEditor form={form} setForm={setForm} clients={clients} objects={objects} />
          <DialogFooter>
            <Button data-testid="save-quote-button" onClick={save} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function emptyQuote() {
  return {
    client_id: "",
    object_id: "",
    project_title: "",
    date: new Date().toLocaleDateString("de-DE"),
    valid_until: "",
    reverse_charge_13b: false,
    intro_text: "",
    status: "draft",
    items: [{ description: "", quantity: 1, unit: "Stk", unit_price: 0 }],
  };
}

function QuoteEditor({ form, setForm, clients, objects }) {
  const [aiBusy, setAiBusy] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestText, setSuggestText] = useState("");

  const addItem = () => setForm({ ...form, items: [...(form.items || []), { description: "", quantity: 1, unit: "Stk", unit_price: 0 }] });
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const editItem = (i, k, v) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: v };
    setForm({ ...form, items });
  };

  const generateIntro = async () => {
    if (!form.project_title) return toast.error("Введите название проекта");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/generate-angebot-intro", {
        project_title: form.project_title, client_id: form.client_id,
        items: (form.items || []).map((it) => ({
          description: it.description, quantity: Number(it.quantity) || 0,
          unit: it.unit || "Stk", unit_price: Number(it.unit_price) || 0,
        })),
      });
      setForm({ ...form, intro_text: data.text });
      toast.success("Вводный текст сгенерирован");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI недоступен");
    } finally { setAiBusy(false); }
  };

  const suggestItems = async () => {
    if (!suggestText.trim()) return toast.error("Опишите проект");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/suggest-items", { description: suggestText });
      const merged = [...(form.items || []).filter((it) => it.description || Number(it.unit_price) > 0), ...data.items];
      setForm({ ...form, items: merged });
      setSuggestOpen(false);
      setSuggestText("");
      toast.success(`Добавлено позиций: ${data.items.length}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI недоступен");
    } finally { setAiBusy(false); }
  };

  const subtotal = (form.items || []).reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const vat = form.reverse_charge_13b ? 0 : subtotal * 0.19;
  const total = subtotal + vat;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Клиент *</Label>
          <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
            <SelectTrigger data-testid="quote-client-select" className="bg-[#0f1521] border-[#212b3d]"><SelectValue placeholder="Выберите клиента" /></SelectTrigger>
            <SelectContent className="bg-[#131924] border-[#212b3d] text-slate-100">
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Объект (опционально)</Label>
          <Select value={form.object_id || "__none"} onValueChange={(v) => setForm({ ...form, object_id: v === "__none" ? "" : v })}>
            <SelectTrigger className="bg-[#0f1521] border-[#212b3d]"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="bg-[#131924] border-[#212b3d] text-slate-100">
              <SelectItem value="__none">— без объекта —</SelectItem>
              {objects.map((o) => <SelectItem key={o.id} value={o.id}>{o.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label>Название проекта *</Label>
          <Input data-testid="quote-title-input" value={form.project_title} onChange={(e) => setForm({ ...form, project_title: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" />
        </div>
        <div><Label>Дата</Label><Input value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} placeholder="ДД.MM.ГГГГ" className="bg-[#0f1521] border-[#212b3d]" /></div>
        <div><Label>Действует до</Label><Input value={form.valid_until || ""} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} placeholder="ДД.MM.ГГГГ" className="bg-[#0f1521] border-[#212b3d]" /></div>
        <div>
          <Label>Статус</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger className="bg-[#0f1521] border-[#212b3d]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#131924] border-[#212b3d] text-slate-100">
              {Object.entries(QUOTE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox data-testid="ustg-13b-checkbox" checked={form.reverse_charge_13b}
              onCheckedChange={(v) => setForm({ ...form, reverse_charge_13b: !!v })}
              className="border-[#212b3d] data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
            <span className="text-sm text-slate-300">Обратное начисление НДС <span className="text-slate-500">(§13b UStG)</span></span>
          </label>
        </div>
        <div className="sm:col-span-2">
          <div className="flex items-center justify-between mb-1.5">
            <Label>Вводный текст (немецкий)</Label>
            <Button type="button" size="sm" onClick={generateIntro} disabled={aiBusy}
              data-testid="ai-generate-intro-button"
              className="h-7 bg-purple-500/15 text-purple-300 border border-purple-500/30 hover:bg-purple-500/25 text-xs">
              {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              AI: сгенерировать
            </Button>
          </div>
          <Textarea value={form.intro_text || ""} onChange={(e) => setForm({ ...form, intro_text: e.target.value })}
            placeholder="Sehr geehrte Damen und Herren, …" className="bg-[#0f1521] border-[#212b3d] min-h-[80px] font-mono text-xs" />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <Label className="text-base">Позиции</Label>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setSuggestOpen(!suggestOpen)} type="button"
              data-testid="ai-suggest-toggle-button"
              className="bg-purple-500/15 text-purple-300 border border-purple-500/30 hover:bg-purple-500/25">
              <Wand2 size={14} /> AI: предложить позиции
            </Button>
            <Button size="sm" onClick={addItem} type="button" className="bg-blue-500/15 text-blue-300 border border-blue-500/30 hover:bg-blue-500/25">
              <Plus size={14} /> Добавить
            </Button>
          </div>
        </div>
        {suggestOpen && (
          <div className="mb-3 p-3 rounded-lg border border-purple-500/30 bg-purple-500/5">
            <Label className="text-xs text-purple-300 mb-1.5 block">Опишите проект по-русски:</Label>
            <Textarea data-testid="ai-suggest-input" value={suggestText} onChange={(e) => setSuggestText(e.target.value)}
              placeholder='Пример: "монтаж гипсокартонных стен в квартире 60 м² + шпаклёвка Q3"'
              className="bg-[#0f1521] border-[#212b3d] min-h-[70px] text-sm" />
            <div className="flex justify-end mt-2">
              <Button size="sm" onClick={suggestItems} disabled={aiBusy} type="button"
                data-testid="ai-suggest-submit-button"
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Сгенерировать
              </Button>
            </div>
          </div>
        )}
        <div className="rounded-lg border border-[#212b3d] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0f1521] text-slate-400 text-xs">
              <tr>
                <th className="text-left px-2 py-2 font-medium">Наименование</th>
                <th className="text-right px-2 py-2 font-medium w-20">Кол-во</th>
                <th className="text-left px-2 py-2 font-medium w-24">Ед.</th>
                <th className="text-right px-2 py-2 font-medium w-28">Цена за ед.</th>
                <th className="text-right px-2 py-2 font-medium w-28">Итого</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#212b3d]">
              {(form.items || []).map((it, i) => (
                <tr key={i}>
                  <td className="p-1"><Input value={it.description} onChange={(e) => editItem(i, "description", e.target.value)} className="bg-transparent border-transparent hover:border-[#212b3d]" /></td>
                  <td className="p-1"><Input type="number" step="0.01" value={it.quantity} onChange={(e) => editItem(i, "quantity", e.target.value)} className="bg-transparent border-transparent hover:border-[#212b3d] text-right" /></td>
                  <td className="p-1"><Input value={it.unit} onChange={(e) => editItem(i, "unit", e.target.value)} className="bg-transparent border-transparent hover:border-[#212b3d]" /></td>
                  <td className="p-1"><Input type="number" step="0.01" value={it.unit_price} onChange={(e) => editItem(i, "unit_price", e.target.value)} className="bg-transparent border-transparent hover:border-[#212b3d] text-right" /></td>
                  <td className="p-1 text-right font-mono text-slate-300 pr-2">{fmtEur((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}</td>
                  <td className="p-1"><button onClick={() => rmItem(i)} className="text-slate-500 hover:text-red-400 p-1"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-[#212b3d] bg-[#0f1521] p-4 text-sm space-y-1 ml-auto max-w-sm">
        <div className="flex justify-between"><span className="text-slate-400">Сумма нетто:</span><span className="font-mono">{fmtEur(subtotal)}</span></div>
        <div className="flex justify-between"><span className="text-slate-400">{form.reverse_charge_13b ? "НДС (§13b UStG):" : "НДС 19%:"}</span><span className="font-mono">{fmtEur(vat)}</span></div>
        <div className="flex justify-between pt-2 border-t border-[#212b3d] text-base font-semibold"><span>Итого:</span><span className="font-mono text-amber-400">{fmtEur(total)}</span></div>
      </div>
    </div>
  );
}
